<template>
  <v-expansion-panel>
    <v-expansion-panel-header>
      <div class="d-flex align-center mr-6">
        <div class="d-flex align-center mr-8" style="min-width: 100px">
          <v-icon left>{{ getFieldTypeIcon(field.type) }}</v-icon>
          <span>{{ formattedOption('moduleFieldTypes', field.type) }}</span>
        </div>
        <span :class="{ 'error--text': !field.title }" class="font-weight-bold">{{
          field.title || 'Title Required *'
        }}</span>
        <v-spacer />
        <v-tooltip top>
          <template #activator="{ on }">
            <v-icon v-show="showErrorTooltip" color="error" v-on="on">error</v-icon>
          </template>
          <div>The following issues must be resolved:</div>
          <ul class="pl-2">
            <li v-for="{ message } in fieldErrors" :key="message">{{ message }}</li>
          </ul>
        </v-tooltip>
      </div>
    </v-expansion-panel-header>
    <v-expansion-panel-content eager>
      <text-input
        :disabled="isLoading"
        :value="field.title"
        label="Title"
        class="mt-2"
        max="35"
        :readonly="readonly"
        counter
        required
        @input="onInput($event, 'title')"
      />
      <text-input
        :value="field.key"
        class="mb-2"
        label="Key"
        hint="Dynamically generated via Title"
        disabled
        persistent-hint
        required
      />

      <select-option-config
        v-if="isSelectField"
        :field="field"
        :is-loading="isLoading"
        :readonly="readonly"
        class="mb-4"
        @input="onInput"
      />

      <div class="d-flex align-center justify-space-between">
        <toggle-input
          :readonly="readonly"
          :disabled="isLoading"
          :value="field.required"
          label="Required Field"
          @input="onInput($event, 'required')"
        />
        <v-btn color="error" depressed small :disabled="isLoading || readonly" @click="onDeleteClick">
          <v-icon left>delete</v-icon>
          <span>Remove</span>
        </v-btn>
      </div>

      <field-block-group
        v-if="isFlexibleField"
        ref="field-block-group"
        v-model="field.fields"
        :duplicate-keys="duplicateKeys"
        :is-loading="isLoading"
        :module-name="moduleName"
        :readonly="readonly"
      />
    </v-expansion-panel-content>
    <v-divider />
  </v-expansion-panel>
</template>

<script>
// Helpers
import { mapGetters } from 'vuex'
import { snakeCase } from 'lodash-es'
// Components
import FieldBlockGroup from '@/views/modules/components/field-block-group'
import SelectOptionConfig from '@/views/modules/components/select-option-config'
import TextInput from '@/components/forms/text-input'
import ToggleInput from '@/components/forms/toggle-input'

export default {
  name: 'FieldBlock',
  components: { FieldBlockGroup, SelectOptionConfig, TextInput, ToggleInput },
  props: {
    duplicateKeys: {
      type: Array,
      default: () => [],
    },
    field: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    moduleName: {
      type: String,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    initialFieldKey: null,
  }),
  computed: {
    ...mapGetters({
      getItemsForOption: 'options/getItemsForOption',
      formattedOption: 'options/formattedOption',
    }),
    fieldErrors() {
      const errors = [
        {
          message: 'Duplicate Key',
          error:
            this.field.key &&
            !!this.duplicateKeys.find(duplicate => {
              const splitKeys = duplicate.split('.')
              return splitKeys[splitKeys.length - 1] == this.field.key
            }),
        },
        {
          message: 'Duplicate Keys in Sub Fields',
          error:
            this.field.key &&
            !!this.duplicateKeys.find(duplicate => {
              const splitKeys = duplicate.split('.')
              return splitKeys.slice(0, splitKeys.length - 1).includes(this.field.key)
            }),
        },
        {
          message: 'Missing Title',
          error: !this.field.title,
        },
        {
          message: 'Missing Dynamic Data value',
          error: this.isSelectField && this.field.dynamic && !this.field.dynamicKey,
        },
        {
          message: 'Missing Select Option data',
          error:
            this.isSelectField &&
            !this.field.dynamic &&
            (!this.field.options.length || this.field.options.some(({ text, value }) => !text || !value)),
        },
        {
          message: 'Missing Dynamic Data value in Sub Field',
          error:
            this.field.fields &&
            this.field.fields.some(({ dynamic, dynamicKey, type }) => type === 'select' && dynamic && !dynamicKey),
        },
        {
          message: 'Missing Select Option data in Sub Field',
          error:
            this.field.fields &&
            this.field.fields.some(
              ({ dynamic, options, type }) =>
                type === 'select' && !dynamic && (!options.length || options.some(({ text, value }) => !text || !value))
            ),
        },
        {
          message: 'Missing Title in Sub Field',
          error: this.field.fields && this.field.fields.some(({ title }) => !title),
        },
      ]

      return errors.filter(({ error }) => !!error)
    },
    isFlexibleField() {
      return this.field.type === 'flexible'
    },
    isSelectField() {
      return this.field.type === 'select'
    },
    showErrorTooltip() {
      return this.fieldErrors && this.fieldErrors.length
    },
    moduleFieldTypes() {
      return this.getItemsForOption('moduleFieldTypes')
    },
    moduleDynamicOptions() {
      return this.getItemsForOption('moduleDynamicOptions')
    },
  },
  created() {
    this.initialFieldKey = this.field.key
  },
  methods: {
    getFieldTypeIcon(type) {
      return this.moduleFieldTypes.find(({ value }) => value === type).icon
    },
    lockKeys() {
      this.initialFieldKey = this.field.key
      if (this.$refs['field-block-group']) this.$refs['field-block-group'].lockKeys()
    },
    onDeleteClick() {
      this.$emit('delete')
    },
    onInput(value, model) {
      // Dynamically update key if it didn't already exist.
      const key =
        this.initialFieldKey || snakeCase(`${this.moduleName} ${model === 'title' ? value : this.field.title}`)
      this.$emit('input', { ...this.field, key, [model]: value })
    },
  },
}
</script>
